/**
 * Football League Sorting Utility
 * Provides intelligent sorting of football matches based on league priority
 * and user location (Brazilian users get Brazilian leagues first)
 */

import { getBrazilianStatus } from './detectBrazil';

// League priority for sorting (higher = more important)
// For Brazilian users: Brasileirão gets +100 bonus making it highest
// For other users: EPL > Brasileirão > La Liga > MLS
export const LEAGUE_PRIORITY = {
  2: 10,   // Champions League - highest for European nights
  39: 9,   // Premier League
  71: 8.5, // Brasileirao Serie A (between EPL and La Liga for non-BR users)
  140: 8,  // La Liga
  78: 7,   // Bundesliga
  135: 7,  // Serie A
  61: 6,   // Ligue 1
  3: 6,    // Europa League
  253: 5,  // MLS
  262: 4,  // Liga MX
  72: 3,   // Brasileirao Serie B
  73: 2,   // Copa do Brasil
  74: 2,   // Brasileirao Serie D
  // Regional Brazilian leagues
  476: 3,  // Paulista Championship
  477: 3,  // Carioca Championship
  475: 3,  // Mineiro Championship
  520: 3,  // Copa do Brasil
  541: 4,  // Copa Libertadores
};

// Brazilian league IDs
export const BRAZILIAN_LEAGUES = [
  71,   // Brasileirao Serie A
  72,   // Brasileirao Serie B
  73,   // Brasileirao Serie C
  74,   // Brasileirao Serie D
  475,  // Mineiro Championship
  476,  // Paulista Championship
  477,  // Carioca Championship
  478,  // Gaucho Championship
  479,  // Baiano Championship
  480,  // Paranaense Championship
  520,  // Copa do Brasil
  541,  // Copa Libertadores (South American but important for Brazil)
];

// Scalable country configuration for future expansion
export const COUNTRY_LEAGUES = {
  'BR': BRAZILIAN_LEAGUES,
  'US': [253, 254, 255], // MLS, USL Championship, USL League One
  'MX': [262],           // Liga MX
  'AR': [128, 129],      // Argentine Primera, Primera B
  // Add more countries as needed
};

// Get user's country code (expandable for more detection methods)
export const getUserCountry = () => {
  // PRIORITY 1: Check Brazil using the proven detectBrazil method
  // This catches localStorage, URL params, and location sensor
  if (getBrazilianStatus && getBrazilianStatus()) {
    return 'BR';
  }

  // PRIORITY 2: Check navigator.language for other countries
  if (typeof navigator !== 'undefined' && navigator.language) {
    const lang = navigator.language.toLowerCase();
    // Don't check pt-br here since getBrazilianStatus handles it better
    if (lang.includes('en-us') || lang === 'en') return 'US';
    if (lang.includes('es-mx')) return 'MX';
    if (lang.includes('es-ar')) return 'AR';
  }

  // PRIORITY 3: Return null - will use default popular leagues
  return null;
};

/**
 * Get the priority score for a league
 * Brazilian users get a bonus for Brazilian leagues
 */
export const getLeaguePriority = (leagueId, isBrazilian = false) => {
  // Convert to number to ensure consistent comparison
  const numericLeagueId = Number(leagueId);
  const basePriority = LEAGUE_PRIORITY[numericLeagueId] || 0;

  // Brazilian users get +100 priority for Brazilian leagues
  if (isBrazilian && BRAZILIAN_LEAGUES.includes(numericLeagueId)) {
    // Remove per-comparison logging to avoid spam
    return basePriority + 100;
  }

  return basePriority;
};

/**
 * Get fixture status priority
 * Lower number = higher priority (shown first)
 */
export const getStatusPriority = (status) => {
  // Live statuses
  if (['1H', '2H', 'HT', 'ET', 'BT', 'P', 'SUSP', 'INT', 'LIVE'].includes(status)) return 1;
  // Upcoming statuses
  if (['NS', 'TBD'].includes(status)) return 2;
  // Completed statuses
  if (['FT', 'AET', 'PEN', 'PST', 'CANC', 'ABD', 'AWD', 'WO'].includes(status)) return 3;
  // Unknown status - treat as upcoming
  return 2;
};

/**
 * Advanced sorting function for football fixtures
 * Combines league priority, user location, and game status
 *
 * @param {Array} fixtures - Array of fixture objects
 * @param {Object} options - Sorting options
 * @param {boolean} options.isBrazilian - Whether user is from Brazil
 * @param {string} options.sortBy - Sort method: 'smart', 'time', 'league'
 * @param {string} options.marketFilter - Filter: 'all', 'live', 'prematch'
 * @param {boolean} options.statusFirst - Prioritize status over league (Live > Upcoming > Finished)
 */
export const sortFootballFixtures = (fixtures, options = {}) => {
  const {
    isBrazilian = false,
    sortBy = 'smart', // 'smart', 'time', 'league'
    marketFilter = 'all', // 'all', 'live', 'prematch'
    statusFirst = false // When true: Status > League > Time
  } = options;

  // First apply market filter if needed
  let filtered = [...fixtures];

  if (marketFilter && marketFilter !== 'all') {
    filtered = filtered.filter(fixture => {
      const status = fixture?.fixture?.status?.short || '';
      const statusPriority = getStatusPriority(status);

      if (marketFilter === 'live') {
        return statusPriority === 1; // Live games only
      } else if (marketFilter === 'prematch') {
        return statusPriority === 2; // Upcoming games only
      }
      return true;
    });
  }


  // Now sort based on sortBy option
  return filtered.sort((a, b) => {
    const leagueIdA = a.league?.id;
    const leagueIdB = b.league?.id;
    const statusA = a.fixture?.status?.short || '';
    const statusB = b.fixture?.status?.short || '';
    const statusPriorityA = getStatusPriority(statusA);
    const statusPriorityB = getStatusPriority(statusB);

    // PRIORITY 1: If statusFirst is enabled, sort by status BEFORE league
    if (statusFirst && statusPriorityA !== statusPriorityB) {
      return statusPriorityA - statusPriorityB; // Live > Upcoming > Finished
    }

    // PRIORITY 2: Sort by league (method depends on sortBy)
    // If user selected 'league' sort, sort alphabetically by league name
    if (sortBy === 'league') {
      const leagueNameA = a.league?.name || '';
      const leagueNameB = b.league?.name || '';
      const leagueCompare = leagueNameA.localeCompare(leagueNameB);
      if (leagueCompare !== 0) return leagueCompare;
    }
    // Smart sort: Use league priority system
    else if (sortBy === 'smart') {
      // Get league priorities (Brazilian users get bonus for Brazilian leagues)
      const priorityA = getLeaguePriority(leagueIdA, isBrazilian);
      const priorityB = getLeaguePriority(leagueIdB, isBrazilian);

      // Sort by league priority (higher priority = shown first)
      if (priorityA !== priorityB) {
        return priorityB - priorityA; // Descending order (higher priority first)
      }
    }
    // For 'time' sort or as secondary sort for same priority leagues

    // PRIORITY 3: Within same league/priority, sort by status (if not already sorted)
    if (!statusFirst && statusPriorityA !== statusPriorityB) {
      return statusPriorityA - statusPriorityB;
    }

    // PRIORITY 4: Within same status, sort by timestamp
    const timestampA = new Date(a.fixture?.date).getTime();
    const timestampB = new Date(b.fixture?.date).getTime();

    // Live and upcoming: earlier games first
    if (statusPriorityA <= 2) {
      return timestampA - timestampB;
    }
    // Completed: most recent first
    return timestampB - timestampA;
  });
};

/**
 * Get a descriptive label for a league
 */
export const getLeagueLabel = (leagueId, leagueName) => {
  // Add emoji flags or badges for important leagues
  const specialLabels = {
    71: '🇧🇷 ' + leagueName,  // Brasileirao
    39: '🏴󠁧󠁢󠁥󠁮󠁧󠁿 ' + leagueName,  // Premier League
    140: '🇪🇸 ' + leagueName, // La Liga
    78: '🇩🇪 ' + leagueName,  // Bundesliga
    135: '🇮🇹 ' + leagueName, // Serie A
    61: '🇫🇷 ' + leagueName,  // Ligue 1
    2: '🏆 ' + leagueName,   // Champions League
    3: '🥈 ' + leagueName,   // Europa League
  };

  return specialLabels[leagueId] || leagueName;
};